import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, Bell, AlignJustify, List } from 'lucide-react';
import { useDensity } from '@/components/admin/context/DensityContext';
import { useAuth } from '@/hooks/useAuth';

const ROUTE_LABELS: Record<string, string> = {
  '/admin/overview': 'Overview',
  '/admin/books': 'Books',
  '/admin/users': 'Users',
  '/admin/loans': 'Loans',
  '/admin/inventory': 'Inventory',
  '/admin/reports': 'Reports',
  '/admin/notifications': 'Notifications',
  '/admin/settings': 'Settings',
  '/admin/content': 'Site Content',
};

const AdminTopbar: React.FC = () => {
  const location = useLocation();
  const { density, toggle } = useDensity();
  const { user } = useAuth();

  const currentLabel = ROUTE_LABELS[location.pathname] ?? 'Admin';

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`
    : (user?.username?.substring(0, 2) ?? 'AD').toUpperCase();

  return (
    <header className="fixed top-0 right-0 left-0 z-30 h-16 bg-white border-b border-[#E8E4DC] flex items-center px-6 gap-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm flex-shrink-0">
        <Link to="/admin/overview" className="text-gray-400 hover:text-gray-600 transition-colors">
          Admin
        </Link>
        <span className="text-gray-300">›</span>
        <span className="font-semibold text-gray-800">{currentLabel}</span>
      </div>

      {/* Global search → opens CommandPalette */}
      <div className="flex-1 flex justify-center">
        <button
          className="relative w-full max-w-md flex items-center gap-2 pl-10 pr-14 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-400 hover:border-amber-400 hover:bg-white transition-colors text-left"
          onClick={() => window.dispatchEvent(new CustomEvent('kg:palette:open'))}
          title="Search or jump to… (⌘K)"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          Search or jump to…
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-gray-400 bg-gray-100 rounded px-1 py-0.5">
            ⌘K
          </span>
        </button>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Density toggle */}
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => density === 'compact' && toggle()}
            className={`p-2 transition-colors ${density === 'comfortable' ? 'bg-amber-100 text-amber-600' : 'text-gray-400 hover:bg-gray-100'}`}
            title="Comfortable"
          >
            <AlignJustify className="h-4 w-4" />
          </button>
          <button
            onClick={() => density === 'comfortable' && toggle()}
            className={`p-2 transition-colors ${density === 'compact' ? 'bg-amber-100 text-amber-600' : 'text-gray-400 hover:bg-gray-100'}`}
            title="Compact"
          >
            <List className="h-4 w-4" />
          </button>
        </div>

        {/* Notifications bell */}
        <Link
          to="/admin/notifications"
          className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        >
          <Bell className="h-5 w-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-500" />
        </Link>

        {/* User avatar */}
        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold ml-1">
          {initials}
        </div>
      </div>
    </header>
  );
};

export default AdminTopbar;
